import {
	buildRecommendedContextStudyTasks,
	resolveQuickStudyRecommendationSessions,
} from "@features/dashboard/recommendations";
import type { RecommendedSkillTask } from "@features/dashboard/recommendations/adapters/dashboard-task-types";
import { useHydratedStudyRecommendations } from "@features/dashboard/recommendations/hooks/useHydratedStudyRecommendations";
import { useAppStore } from "@hooks/useAppStore";
import type { MixedItemDist, MixedItemTag } from "@memorang/types";
import { useTranslation } from "react-i18next";
import { defaultDistributionFilterItems } from "../../../session-config/constants/options";
import { buildRecommendedSkillTasks } from "./helpers";

const hydratedTasks = ({
	hydrated,
	studyPackData,
	onSelectTags,
	onStart,
	translate,
}: {
	hydrated: ReturnType<typeof useHydratedStudyRecommendations>;
	studyPackData?: MixedItemDist;
	onSelectTags?: (tagIds: string[]) => void;
	onStart?: (task: RecommendedSkillTask) => void;
	translate: ReturnType<typeof useTranslation>["t"];
}): RecommendedSkillTask[] =>
	hydrated.status === "READY"
		? buildRecommendedContextStudyTasks({
				result: {
					status: "READY",
					examId: hydrated.context.exam.id,
					sessions: hydrated.sessions,
				},
				tags: studyPackData?.tags ?? [],
				translate,
				onSelectTags,
				onStart,
			})
		: [];

export const useRecommendedStudyTasks = ({
	skills,
	proficiencies,
	questionTypes,
	studyPackData,
	examCollectionIds,
	preferredSkillIds,
	targetLevelId,
	onSelectTags,
	onStart,
}: {
	skills: MixedItemTag[];
	proficiencies: MixedItemTag[];
	questionTypes: MixedItemTag[];
	studyPackData?: MixedItemDist;
	examCollectionIds: number[];
	preferredSkillIds?: string[];
	targetLevelId?: string;
	onSelectTags?: (tagIds: string[]) => void;
	onStart?: (task: RecommendedSkillTask) => void;
}) => {
	const { t } = useTranslation();
	const proficiencyLevels = useAppStore((state) =>
		state.app.proficiencyLevels?.sort((a, b) => a.sortOrder - b.sortOrder),
	);
	const distributionFilterItems = useAppStore(
		(state) =>
			state.app.sessionConfig?.filters || defaultDistributionFilterItems,
	);
	const crossTagSelection = useAppStore(
		(state) => state.app.sessionConfig?.crossTagSelection,
	);
	const hydrated = useHydratedStudyRecommendations({
		sessionCount: Math.max(skills.length, 1),
		durationMinutes: 10,
		onePerTrack: true,
		additionalExamCollectionIds: examCollectionIds,
	});
	const fallbackSessions = buildRecommendedSkillTasks({
		skills,
		proficiencies,
		questionTypes,
		proficiencyLevels,
		preferredSkillIds,
		currentProficiencyLevel: targetLevelId,
		distributionFilterItems,
		crossTagSelection,
		t,
		onSelectTags,
	});
	return {
		tasks: resolveQuickStudyRecommendationSessions({
			studyRecommendationEnabled: hydrated.status !== "DISABLED",
			studyRecommendationLoading: hydrated.status === "LOADING",
			studyRecommendationSessions: hydratedTasks({
				hydrated,
				studyPackData,
				onSelectTags,
				onStart,
				translate: t,
			}),
			fallbackSessions,
		}),
		isLoading: hydrated.status === "LOADING",
	};
};
